import {
  CaseAnalysisSectionSchema,
  SettingsSectionSchema,
  type AgentUiActionId,
  type CaseAnalysisSection,
  type ResolvedAgentUiAction,
  type SettingsSection,
} from '@hector/shared-types';

export const GLOBAL_AGENT_UI_ACTION_CAPABILITIES = [
  'documents.preview',
  'bordereau.open_selection_for_stamping',
  'case_analysis.open_section',
  'settings.open_section',
  'help.open_center',
] as const satisfies readonly AgentUiActionId[];

export type GlobalAgentUiActionId = (typeof GLOBAL_AGENT_UI_ACTION_CAPABILITIES)[number];
export type GlobalAgentUiAction = Extract<ResolvedAgentUiAction, { id: GlobalAgentUiActionId }>;

type Handler<ActionId extends GlobalAgentUiActionId> = (
  action: Extract<GlobalAgentUiAction, { id: ActionId }>,
) => Promise<void>;

export type AgentUiActionHandlers = {
  [ActionId in GlobalAgentUiActionId]: Handler<ActionId>;
};

export interface AgentUiActionDispatchContext {
  readonly capabilities: readonly AgentUiActionId[];
  readonly handlers: AgentUiActionHandlers;
}

/** Builds a dossier route exclusively from the shared closed enum. */
export function buildCaseAnalysisDestination(caseId: string, section: CaseAnalysisSection): string {
  return `/cases/${caseId}?section=${CaseAnalysisSectionSchema.parse(section)}`;
}

/**
 * Ancre de destination pour chaque section réglable proposée par l'agent.
 *
 * Les quatre sections ont quitté `/settings` pour la page Personnalisation : le
 * nom d'enum décrit toujours le RÉGLAGE (contrat partagé avec le backend, qui le
 * cite dans la description du tool), mais l'URL, elle, a bougé. Sans cette table
 * le bouton proposé par l'agent posait l'avocat sur `/settings#memory`, un
 * onglet qui n'existe plus — panneau vide, aucune erreur, aucun indice.
 *
 * `charte/...` est un deep-link à deux segments consommé par `CharteGraphiqueHub`
 * (`resolveInitialDetail`) : il ouvre directement le détail, pas la liste.
 */
const SETTINGS_SECTION_ANCHORS: Readonly<Record<SettingsSection, string>> = {
  memory: 'memoire',
  skills: 'competences',
  bordereau: 'charte/tampon',
  nomenclature: 'charte/libelles',
};

/** Builds a settings route exclusively from the shared closed enum. */
export function buildSettingsDestination(section: SettingsSection): string {
  return `/personnalisation#${SETTINGS_SECTION_ANCHORS[SettingsSectionSchema.parse(section)]}`;
}

export function buildHelpCenterDestination(): '/aide' {
  return '/aide';
}

/**
 * Executes one globally registered action after rechecking the host snapshot.
 * The card filter is UX only; this guard is the execution boundary.
 */
export async function dispatchAgentUiAction(
  action: ResolvedAgentUiAction,
  context: AgentUiActionDispatchContext,
): Promise<void> {
  if (!context.capabilities.includes(action.id)) {
    throw new Error(
      `Agent UI action is unavailable in this host: actionId=${action.id}; available=${context.capabilities.join(',') || 'none'}. Refresh the conversation or use an advertised action.`,
    );
  }

  switch (action.id) {
    case 'documents.preview':
      return context.handlers['documents.preview'](action);
    case 'bordereau.open_selection_for_stamping':
      return context.handlers['bordereau.open_selection_for_stamping'](action);
    case 'case_analysis.open_section':
      return context.handlers['case_analysis.open_section'](action);
    case 'settings.open_section':
      return context.handlers['settings.open_section'](action);
    case 'help.open_center':
      return context.handlers['help.open_center'](action);
    case 'bordereau.navigate_current_to_stamping':
    case 'bordereau.navigate_current_to_numbering_review':
      throw new Error(
        `Agent UI action has no global handler: actionId=${action.id}. Execute it only from the already-mounted bordereau host.`,
      );
  }
}
